/**
 * Entry application component used to compose providers and render Routes.
 * */

import React, { useEffect } from "react";
import { Provider } from "react-redux";
import { BrowserRouter, useLocation  } from "react-router-dom";
import { PersistGate } from "redux-persist/integration/react";
import { Routes } from "./Routes";
import { I18nProvider } from "../_metronic/i18n";
import { LayoutSplashScreen, MaterialThemeProvider } from "../_metronic/layout";
import "./style.css";
import "../app/modules/User/pages/userProfile/tax-settings/taxStyles.css";
import { useGlobalAITaskRecovery } from "./hooks/useGlobalAITaskRecovery";

import { clearBackgroundTask } from "./modules/Common/utils";
import SocketConfigurationButton from "./modules/Socket/SocketConfigurationButton";

// Routes rendered in Routes.js that are reachable without being a logged-in tenant user
// (public invoice/payment links, the box-label QR landing page, static info pages) — the
// scanner-pairing button is an internal staff tool and has no business showing on these.
const PUBLIC_ROUTE_PREFIXES = ["/auth", "/reset-password", "/status", "/privacy", "/sale/invoice/", "/pilot", "/plaid-oauth", "/box-view", "/bv", "/logout"];

// Hides SocketConfigurationButton on public routes; only useLocation() (needs Router context)
// can make that call, so this gate has to live inside <BrowserRouter>, not alongside it.
function ScannerConfigurationGate() {
  const location = useLocation();
  const isPublicRoute = PUBLIC_ROUTE_PREFIXES.some((prefix) => location.pathname.startsWith(prefix));

  if (isPublicRoute) return null;
  return <SocketConfigurationButton />;
}

export default function App({ store, persistor, basename }) {
  useGlobalAITaskRecovery();

  const handleKeyPress = (event) => {
    // Check if Ctrl + Shift + L keys are pressed to clear track of background tasks
    if (event.ctrlKey && event.shiftKey && event.code === "KeyL") {
      event.preventDefault();
      event.stopPropagation();

      const confirmed = window.confirm("Are you sure you want to clear track of background tasks?");
      if (confirmed) {
        clearBackgroundTask();
      }
    }
  };

  useEffect(() => { 
    // Add event listener for keydown events
    window.addEventListener("keydown", handleKeyPress, true);
    // Cleanup the event listener when the component is unmounted
    return () => {
      window.removeEventListener("keydown", handleKeyPress, true);
    };
  }, []);


 try {
  const href = window.location.origin.toLowerCase();
  if (!href.includes("app") && !href.includes("appp")) {
  const script = document.createElement("script");
  script.src = "https://cdn.jsdelivr.net/npm/eruda";
  script.async = true;
  document.body.appendChild(script);

  script.onload = () => {
    window.eruda?.init();
  };

  console.log(navigator, navigator.userAgent, "navigator");
  }
} catch (err) {
  console.error(err);
}

  return (
      <Provider store={store}>
        {/* Asynchronously persist redux stores and show `SplashScreen` while it's loading. */}
        <PersistGate persistor={persistor} loading={<LayoutSplashScreen />}>
          {/* Add high level `Suspense` in case if was not handled inside the React tree. */}
          <React.Suspense fallback={<LayoutSplashScreen />}>
            {/* Override `basename` (e.g: `homepage` in `package.json`) */}
            <BrowserRouter basename={basename}>
              <ScannerConfigurationGate />
              {/*This library only returns the location that has been active before the recent location change in the current window lifetime.*/}
              <MaterialThemeProvider>
                {/* Provide `react-intl` context synchronized with Redux state.  */}
                <I18nProvider>
                  {/* Render routes with provided `Layout`. */}
                  <Routes />
                </I18nProvider>
              </MaterialThemeProvider>
            </BrowserRouter>
          </React.Suspense>
        </PersistGate>
      </Provider>
    /* Provide Redux store */
  );
}
